import React, { useState } from 'react';
import { Lock, Building2, Flame, Wind } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const [form, setForm]       = useState({ code: '', email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.code.trim()) { setError('Company code is required.'); return; }
    setLoading(true);
    try {
      await login(form.email, form.password, form.code.trim().toUpperCase());
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* ── Left hero — VayuSarthi branding ──────────────────────────────── */}
      <div
        className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #050510 0%, #0a0a1a 50%, #0d0d0d 100%)' }}
      >
        {/* Decorative radial glow */}
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 30% 40%, rgba(99,102,241,0.12) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 70% 80%, rgba(249,115,22,0.08) 0%, transparent 60%)',
          }}
        />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Top logo mark */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }}>
            <Wind size={20} className="text-white" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-black text-white text-[15px] tracking-widest uppercase">VayuSarthi</div>
            <div className="text-[10px] text-indigo-400 tracking-[0.2em] uppercase font-semibold">LPG Management</div>
          </div>
        </div>

        {/* Center wordmark */}
        <div className="relative z-10 flex-1 flex flex-col justify-center">
          {/* Large VS monogram */}
          <div
            className="font-black leading-none tracking-tight mb-6 select-none"
            style={{
              fontSize: 'clamp(72px, 9vw, 120px)',
              background: 'linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #6366f1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            VS
          </div>
          <h2 className="font-black text-3xl text-white tracking-tight leading-snug mb-3">
            Every cylinder.<br />Every challan.<br />One platform.
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
            Fast entry, live stock tracking, pending payments, cylinder
            rotation and instant PDF ledgers — built for LPG traders.
          </p>

          {/* Feature tags */}
          <div className="flex flex-wrap gap-2 mt-6">
            {['Live Stock', 'Challan Tracking', 'Payment Ledger', 'Multi-Godown'].map(tag => (
              <span key={tag}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/10 text-gray-400">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="relative z-10">
          <div className="text-[11px] text-gray-600 tracking-wider uppercase font-semibold">
            Powered by VayuSarthi
          </div>
        </div>
      </div>

      {/* ── Right — sign-in form ──────────────────────────────────────────── */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 bg-white">
        <div className="w-full max-w-md mx-auto">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }}>
              <Wind size={18} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-black text-[15px] tracking-widest uppercase leading-none">VayuSarthi</div>
              <div className="text-[10px] text-indigo-500 tracking-[0.15em] uppercase font-semibold">LPG Management</div>
            </div>
          </div>

          <div className="small-label text-gray-500 mb-2">Sign in</div>
          <h1 className="font-black text-4xl sm:text-5xl tracking-tight mb-2">Welcome back.</h1>
          <p className="text-gray-500 mb-8 text-sm">Enter your company code and credentials.</p>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="small-label block mb-2">Company Code</label>
              <div className="relative">
                <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  className="input-lg pl-9 uppercase tracking-widest font-semibold"
                  type="text"
                  placeholder="e.g. DEMO"
                  value={form.code}
                  onChange={e => set('code', e.target.value)}
                  maxLength={8}
                  required
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Your company's unique login code</p>
            </div>
            <div>
              <label className="small-label block mb-2">Email</label>
              <input className="input-lg" type="email" placeholder="you@company.com"
                autoComplete="email" value={form.email}
                onChange={e => set('email', e.target.value)} required />
            </div>
            <div>
              <label className="small-label block mb-2">Password</label>
              <input className="input-lg" type="password" placeholder="••••••••"
                autoComplete="current-password" value={form.password}
                onChange={e => set('password', e.target.value)} required />
            </div>
            <button
              type="submit"
              className="w-full h-12 flex items-center justify-center gap-2 font-semibold text-white rounded-lg transition-all"
              style={{ background: loading ? '#818cf8' : 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
              disabled={loading}
            >
              <Lock size={16} />
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-400">
            Super admin?{' '}
            <a href="/superadmin/login" className="text-gray-700 font-semibold hover:underline">
              Admin panel →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
